import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BadgeComponent } from '../../../../../shared/components/badge/badge.component';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import { TabComponent } from '../../../../../shared/components/tabs/tab/tab.component';
import { TabsComponent } from '../../../../../shared/components/tabs/tabs.component';
import { TagComponent } from '../../../../../shared/components/tag/tag.component';
import { AccordionModule } from 'primeng/accordion';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TextareaModule } from 'primeng/textarea';
import { Menu, MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { BasicTableComponent } from '../../basic-table/basic-table.component';
import { RideTimelineTooltipComponent } from '../../ride-timeline-tooltip/ride-timeline-tooltip.component';
import { RequestBuilderService } from '../../../../../shared/services/request-builder.service';
import { NotesComponent } from '../../../../../shared/components/notes/notes.component';
import { RideActionsService } from '../../../services/ride-actions.service';
import { IStep } from '../../../../../shared/interfaces/step.interface';
import { MenuItem } from 'primeng/api';
import { changeTime } from '../../../../../shared/utilities/utility-functions';
import { ToggleMenuService } from '../../../../../shared/services/toggle-menu.service';
import { cargoActionType } from '../../../../../shared/enums/rideStopCargoStatus';
import { AuthService } from '../../../../../core/auth/services/auth.service';
import { UserRole } from '../../../../../core/auth/enums/userRoles';
import { NoRecordsComponent } from '../../../../../shared/components/no-records/no-records.component';
interface EventItem {
  note?: string;
  title?: string;
  date?: string;
  icon?: string;
  color?: string;
  image?: string;
}

@Component({
  selector: 'app-ride-activity-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    TabsComponent,
    TabComponent,
    TextareaModule,
    BasicTableComponent,
    AccordionModule,
    TagComponent,
    BadgeComponent,
    MenuModule,
    TooltipModule,
    RideTimelineTooltipComponent,
    NotesComponent,
    NoRecordsComponent,
  ],
  templateUrl: './ride-activity-dialog.component.html',
  styleUrl: './ride-activity-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RideActivityDialogComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly rideActionsService = inject(RideActionsService);
  private readonly dialogService = inject(DialogService);
  private readonly toggleMenuService = inject(ToggleMenuService);
  private readonly authService = inject(AuthService);

  note = '';
  loading = false;
  notes = signal<EventItem[]>([]);
  activityLogTableData = signal<any>({
    columns: [
      { name: 'id', displayName: '#' },
      { name: 'actionType', displayName: 'Action' },
      { name: 'createdBy', displayName: 'Done by' },
      { name: 'doneAtUtc', displayName: 'Done At' },
      { name: 'doneAtAddress', displayName: 'Done at Address' },
    ],
    data: [],
  });

  GPSPingsData = signal<any>({
    columns: [
      { name: 'doneAtUtc', displayName: 'Timestamp' },
      { name: 'doneAtLatitude', displayName: 'Latitude' },
      { name: 'doneAtLongitude', displayName: 'Longitude' },
      { name: 'actionType', displayName: 'Stop Type' },
    ],
    data: [],
  });
  cargoActionType = signal(cargoActionType);

  accordionActiveIndex: number | undefined = 0;
  rideStopMenuItems = signal<MenuItem[]>([]);

  appSetting = this.authService.appSetting();
  isClient = (localStorage.getItem('role') ?? '').toLowerCase() === UserRole.Client;

  openMenu(event: Event, menu: Menu) {
    this.toggleMenuService.openMenu(menu, event);
  }

  setRideStopMenuItems(e: any, stop: IStep, stopMenuRef: any, index: number) {
    this.rideStopMenuItems.set([
      {
        label: 'View Activity Log',
        command: () => this.viewActivityLog(stop),
      },
    ]);

    this.openMenu(e, stopMenuRef);
  }

  viewActivityLog(stop: any) {
    this.rideActionsService.WaypointsActivityLogDialog(this.dialogService, stop.originalRideId, stop.stopId, this.config.data.rideTimeline, this.config.data.timezoneOffsetInSeconds);
  }

  ngOnInit() {
    if (this.appSetting.viewActivityLog) this.getRideActivityLog();
    if (this.appSetting.viewGPSPingsData) this.getGPSPingsData();
    if (this.appSetting.viewNoteActivityLog) this.getNote();
  }

  getRideActivityLog() {
    this.requestBuilder.get(`rides/${this.config.data.uuid}/activity-logs`).subscribe({
      next: res => {
        this.activityLogTableData.update(prev => {
          return {
            ...prev,
            data: this.replaceNullValuesInArray(res.data),
          };
        });
      },
      error: error => {},
    });
  }

  getGPSPingsData() {
    this.requestBuilder.get(`rides/${this.config.data.uuid}/gps-pings-activity-logs`).subscribe({
      next: res => {
        console.log(res);
        this.GPSPingsData.update(prev => {
          return {
            ...prev,
            data: this.replaceNullValuesInArray(res.data, 'GPSPingsData'),
          };
        });
      },
      error: error => {},
    });
  }

  replaceNullValuesInArray(dataArray: any, dataName?: string): any {
    return dataArray.map((obj: any) => {
      const updatedObject: any = { ...obj }; // Create a shallow copy of the object
      for (const key in updatedObject) {
        if (updatedObject[key] === null || updatedObject[key] === undefined || updatedObject[key] === '') {
          updatedObject[key] = 'N/A';
        } else if (key == 'doneAtUtc' && dataName != 'GPSPingsData') {
          updatedObject[key] = changeTime(updatedObject[key] + 'z', 'MM/DD/YYYY hh:mm A', this.config.data.timezoneOffsetInSeconds);
        } else if (key == 'doneAtUtc' && dataName == 'GPSPingsData') {
          updatedObject[key] = changeTime(updatedObject[key] + 'z', 'hh:mm:ss A', this.config.data.timezoneOffsetInSeconds);
        }
      }
      return updatedObject;
    });
  }

  addNote() {
    const body = {
      rideId: this.config.data.dispatchId,
      internalNote: this.note,
    };
    this.loading = true;
    this.requestBuilder.post(`rides/note`, body).subscribe({
      next: () => {
        this.loading = false;

        this.note = '';
        this.getNote();
      },
      error: error => {
        this.loading = false;
      },
    });
  }

  getNote() {
    this.requestBuilder.get(`rides/${this.config.data.uuid}/note-activity-logs`).subscribe({
      next: res => {
        this.notes.set(
          res.data.map((item: any) => ({
            title: item.createdBy,
            note: item.internalNote,
            date: changeTime(item.doneAtUtc + 'z', 'MM/DD/YYYY hh:mm A', this.config.data.timezoneOffsetInSeconds),
            icon: 'icon-pencil-simple',
          })),
        );
        this.notes();
      },
      error: err => {
        console.error('Error fetching Service Regions Filters:', err);
      },
    });
  }

  toggleActiveAccordionTab(index: number) {
    this.accordionActiveIndex = this.accordionActiveIndex == index ? -1 : index;
    this.toggleMenuService.closeMenu();
  }

  accordionActiveIndexChange(index: any) {
    this.accordionActiveIndex = index;
  }
}
